import React, { useState, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import ClickOutside from '../ClickOutside';
import Popup from '../Popup';
import Container from '../Container';
import useMedia from '../../hooks/useMedia';
import IconAppMenu from './IconAppMenu';
import IconBusinessProfile from './IconBusinessProfile';
import IconLsOnline from './IconLsOnline';
import IconNucleus from './IconNucleus';
// import IconScoping from './IconScoping';
import Text from '../Text';
import Anchor from '../Anchor';
import IconUserManagement from './IconUserManagement';
import IconWebsiteTemplates from './IconWebsiteTemplates';
import IconBudgetTracker from './IconBudgetTracker';
import IconStaffPortal from './IconStaffPortal';

const envUrls = {
  development: {
    localsearchOnline: 'https://development.ls-www.localsearch.cloud',
    nucleus: 'https://development.ls-dashboard.localsearch.cloud',
    localsearchBusinessProfile:
      'https://development.ls-www.localsearch.cloud/manage-listing',
    accountSettings: 'https://development.ls-user.localsearch.cloud',
    websiteTemplates: 'http://webtemplates.localsearch.com.au',
    budgetTracker: 'https://development.ls-budget-tracker.localsearch.cloud',
    staffPortal: 'https://development.ls-digital-presence.localsearch.cloud',
  },
  staging: {
    localsearchOnline: 'https://staging.ls-www.localsearch.cloud',
    nucleus: 'https://staging.ls-dashboard.localsearch.cloud',
    localsearchBusinessProfile:
      'https://staging.ls-www.localsearch.cloud/manage-listing',
    accountSettings: 'https://staging.ls-user.localsearch.cloud',
    websiteTemplates: 'http://webtemplates.localsearch.com.au',
    budgetTracker: 'https://staging.ls-budget-tracker.localsearch.cloud',
    staffPortal: 'https://staging.ls-digital-presence.localsearch.cloud',
  },
  production: {
    localsearchOnline: 'https://www.localsearch.com.au',
    nucleus: 'https://nucleus.localsearch.com.au',
    localsearchBusinessProfile: 'https://www.localsearch.com.au/manage-listing',
    accountSettings: 'https://account.localsearch.com.au',
    websiteTemplates: 'http://webtemplates.localsearch.com.au',
    budgetTracker: 'https://budget-tracker.localsearch.com.au',
    staffPortal: 'https://digital-presence.localsearch.cloud',
  },
};

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0;
  transition: all ease 0.3s;
  background: none;
  border: none;
  &:focus {
    outline: none;
  }
`;

const StyledAnchor = styled(Anchor)`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 33.3333%;
  padding: 0 5px;
  margin-top: 15px;
  margin-bottom: 15px;
  flex-direction: column;
  border: none;
  background: none;
  &:hover span {
    color: ${props => props.theme.primary.normal};
  }
`;

const AppMenu = ({
  iconFill,
  iconFillActive,
  hide,
  env,
  onOpen,
  onClose,
  isStaff,
  isPersoniv,
  ...props
}) => {
  const [show, setShow] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);

  const theme = useContext(ThemeContext);
  const { mobile } = useMedia(theme.mediaQueries);

  const links = envUrls[env] || envUrls.production;

  const menuItems = [
    {
      text: 'Localsearch',
      icon: <IconLsOnline />,
      href: links.localsearchOnline,
    },
    {
      text: 'Localsearch For Business',
      icon: <IconBusinessProfile />,
      href: links.localsearchBusinessProfile,
    },
    {
      text: 'Nucleus',
      icon: <IconNucleus />,
      href: links.nucleus,
    },
    // {
    //   text: 'Localsearch Scoping',
    //   icon: <IconScoping />,
    //   href: '',
    // },
    ...(isStaff
      ? [
          {
            text: 'Budget Tracker',
            icon: <IconBudgetTracker />,
            href: links.budgetTracker,
          },
        ]
      : []),
    ...(isStaff
      ? [
          {
            text: 'Website Templates',
            icon: <IconWebsiteTemplates />,
            href: links.websiteTemplates,
          },
        ]
      : []),
    ...(isStaff || isPersoniv
      ? [
          {
            text: 'Add a Business Staff Portal',
            icon: <IconStaffPortal />,
            href: links.staffPortal,
          },
        ]
      : []),
    {
      text: 'Account Settings',
      icon: <IconUserManagement />,
      href: links.accountSettings,
    },
  ];

  return (
    <ClickOutside
      onClickOutside={() => {
        setShow(false);
        if (show) {
          onClose();
        }
      }}
      {...props}
    >
      <StyledButton
        onMouseEnter={() => setButtonHover(true)}
        onMouseLeave={() => setButtonHover(false)}
        onClick={() => {
          setShow(!show);
          onOpen();
        }}
        type="button"
      >
        <IconAppMenu fill={buttonHover || show ? iconFillActive : iconFill} />
      </StyledButton>
      <Popup
        width={mobile ? 'calc(100vw - 20px)' : '420px'}
        minWidth="auto"
        position={mobile ? 'fixed' : 'absolute'}
        top={mobile ? 'auto' : '52px'}
        left={mobile ? '10px' : 'auto'}
        right={mobile ? '10px' : '0'}
        marginTop={mobile ? '27px' : '8px'}
        show={show}
        anchor="right"
      >
        <Container
          padding={mobile ? '30px 10px 10px 10px' : '30px 30px 10px 30px'}
          display="flex"
          justContent="center"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Text
            textAlign="center"
            color="normal"
            fontSize="20px"
            fontFamily="circular"
            fontWeight="bold"
            marginBottom="10px"
            width="100%"
            lineHeight="1.2"
          >
            Your Localsearch Suite
          </Text>
          <Container
            width="100%"
            display="flex"
            flexWrap="wrap"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {menuItems.map(({ icon, text, href }) => {
              if (hide.includes(text)) return null;
              return (
                <StyledAnchor key={text} href={href} target="_blank">
                  <Container maxWidth="94px" width="100%">
                    {icon}
                  </Container>
                  <Text
                    lineHeight="1.3"
                    marginTop="10px"
                    color="normal"
                    fontFamily="circular"
                    fontWeight="600"
                    fontSize="14px"
                    transition="all 0.3s ease"
                  >
                    {text}
                  </Text>
                </StyledAnchor>
              );
            })}
          </Container>
        </Container>
      </Popup>
    </ClickOutside>
  );
};

AppMenu.propTypes = {
  iconFill: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  iconFillActive: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  hide: PropTypes.arrayOf(PropTypes.string),
  env: PropTypes.string,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isStaff: PropTypes.bool,
  isPersoniv: PropTypes.bool,
};

AppMenu.defaultProps = {
  iconFill: 'white',
  iconFillActive: '#97AEFF',
  hide: [],
  env: 'production',
  onOpen: () => {},
  onClose: () => {},
  isStaff: false,
  isPersoniv: false,
};

export default AppMenu;
